"use client";

import Pricing from "@/components/ProductCard/Pricing/Pricing";
import QuantityChanger from "@/components/QuantityChanger/QuantityChanger";
import StockAvailability from "@/components/StockAvailability/StockAvailability";
import AddToWishlistCTA from "@/components/UI/AddToWishlistCTA/AddToWishlistCTA";
import Button from "@/components/UI/Button/Button";
import Notification from "@/components/UI/Notification/Notification";
import {
	getCartDatalayerDetails,
	getItemListData,
	useDataLayerContext,
} from "@/datalayer/DataLayerContextProvider";
import checkUserHasRolePermission from "@/hooks/checkUserHasRolePermission";
import { useFireDataLayerCartEvents } from "@/hooks/useFireDataLayerEvents";
import { useI18n } from "@/i18n/client";
import { ProductData } from "@/lib/5874/types";
import { useSessionContext } from "@/lib/auth/SessionContextProvider";
import { getCustomField } from "@/lib/bigcommerce/utils/bigcommerceCustomFieldsUtils";
import { useCartContext } from "@/providers/CartContextProvider";
import { useProductContext } from "@/providers/ProductContextProvider";
import { getLineItemForProduct } from "@/utils/cart-utils";
import { FC, useMemo, useState } from "react";
import {
	handleAddToCart,
	handleUpdateCartLineItem,
} from "../ProductForm/_actions/addToCart";
import styles from "./ProductKitForm.module.scss";
import ProductKitItem from "./ProductKitItem/ProductKitItem";

interface ProductKitFormProps {
	fixedPrice: boolean;
	isNotPurchasable: boolean;
	showAddToCart: boolean;
}

interface GroupedProducts {
	label: string;
	products: ProductData[];
}

const ProductKitForm: FC<ProductKitFormProps> = ({
	fixedPrice,
	isNotPurchasable,
	showAddToCart = true,
}) => {
	const { product: parent } = useProductContext();
	const { session } = useSessionContext();
	const { fireCartEvent } = useFireDataLayerCartEvents();
	const t = useI18n();
	const { cart, setCart, setIsModifyingCart, setConfirmationProduct } =
		useCartContext();
	const [kitProductData, setKitProductData] = useState<ProductData[]>(
		parent.kitProducts || [],
	);
	const [showRemoveMessage, setShowRemoveMessage] = useState(false);
	const lineItem = cart ? getLineItemForProduct(cart, parent) : undefined;
	const [quantity, setProductQuantity] = useState(lineItem?.quantity || 0);

	// Datalayer
	const { addToCart } = useDataLayerContext();
	const { itemListId, itemListName } = getItemListData(parent);
	const cartDetails = getCartDatalayerDetails(cart);
	const datalayerProducts = kitProductData.map((p) => ({
		product: p,
		quantity: p.kitQuantity ?? 1,
		variantId: p.id,
	}));

	// Group products based on their family group
	const groupedProducts = useMemo<Record<string, GroupedProducts>>(() => {
		const familyMap = new Map<string, GroupedProducts>();

		for (const product of kitProductData) {
			const family =
				getCustomField(product, "Akeneo|FamilyCode")?.value ||
				getCustomField(product, "Akeneo|Family")?.value ||
				"other";

			const familyLabel =
				getCustomField(product, "Akeneo|Family")?.value ||
				getCustomField(product, "Akeneo|FamilyCode")?.value ||
				"Other";

			if (!familyMap.has(family)) {
				familyMap.set(family, {
					label: familyLabel,
					products: [],
				});
			}

			familyMap.get(family)!.products.push(product);
		}

		return Object.fromEntries(familyMap);
	}, [parent.kitProducts, kitProductData]);

	const kitPrice = useMemo(() => {
		// Add the totals of each item in the kit
		const price = kitProductData.reduce((acc, product) => {
			const price =
				!product?.pricesExTax?.basePrice?.value || !product.kitQuantity
					? 0
					: product?.pricesExTax?.basePrice?.value * product.kitQuantity;

			return acc + price;
		}, 0);

		return price;
	}, [parent.kitProducts, kitProductData]);

	const addProductToCart = async (qty?: number) => {
		const mappedKitData = kitProductData
			.map((product) => ({
				id: product.id,
				quantity: product.kitQuantity,
			}))
			.filter((product) => product.quantity > 0);

		setIsModifyingCart(true);

		let res: any;
		if (fixedPrice) {
			if (lineItem !== undefined) {
				res = await handleUpdateCartLineItem(
					{ id: parent.id, quantity: qty || 1 },
					lineItem,
				);

				setProductQuantity(qty || 0);
			} else {
				setProductQuantity(1);
				res = await handleAddToCart([{ id: parent.id, quantity: 1 }]);
			}
		} else {
			res = await handleAddToCart(mappedKitData);
		}

		if (res.status === "success") {
			setCart(res.data);
			if (res.data && !lineItem) {
				setConfirmationProduct(parent);
			} else if (qty === 0) {
				handleRemovedFromCart();
			}
		}

		setIsModifyingCart(false);
	};

	const handleRemovedFromCart = () => {
		setShowRemoveMessage(true);
		setTimeout(() => setShowRemoveMessage(false), 3000);
	};

	return (
		<>
			<div className={styles.kitsOuter}>
				{Object.entries(groupedProducts)?.map(([family, group], index) => (
					<div key={`${family}-${index}`} className={styles.kitSection}>
						<h3 className={styles.kitSectionTitle}>{group.label}</h3>
						<div className={styles.productList}>
							{Array.isArray(group.products) &&
								group.products.map((product: ProductData) => (
									<ProductKitItem
										key={product.id}
										product={product}
										isFixedPrice={fixedPrice}
										allKitProducts={kitProductData}
										setKitProductData={setKitProductData}
										parentIsNotPurchasable={isNotPurchasable}
									/>
								))}
						</div>
					</div>
				))}
			</div>
			{kitProductData &&
				!isNotPurchasable &&
				checkUserHasRolePermission("purchase_enable") &&
				showAddToCart && (
					<div className={styles.actions}>
						{fixedPrice && lineItem !== undefined && quantity > 0 ? (
							<>
								<QuantityChanger
									cart={cart}
									minValue={0}
									value={lineItem.quantity}
									onChange={(quantity) => addProductToCart(quantity)}
									debounceTime={600}
								/>
								{checkUserHasRolePermission("update_shopping_list_item") && (
									<AddToWishlistCTA product={parent} userId={session?.userId} />
								)}
							</>
						) : (
							<>
								<Button
									as='button'
									type='button'
									className={styles.cartButton}
									onClick={() => {
										fireCartEvent("increase", 1, cart, parent);
										addToCart({
											products: datalayerProducts,
											itemListId: itemListId,
											itemListName: itemListName,
											cart: cartDetails,
										});
										return addProductToCart(1);
									}}
								>
									{t("cart.addToCart")}
								</Button>

								{fixedPrice &&
									checkUserHasRolePermission("update_shopping_list_item") && (
										<AddToWishlistCTA
											product={parent}
											userId={session?.userId}
										/>
									)}
							</>
						)}
					</div>
				)}
			{fixedPrice && (
				<StockAvailability
					product={parent}
					className={styles.stockAvailability}
				/>
			)}
			{!isNotPurchasable && ((fixedPrice && lineItem) || !fixedPrice) && (
				<div className={styles.total}>
					<span className={styles.totalText}>{t("cart.total")}:</span>
					<Pricing
						className={styles.price}
						staticPrice={!fixedPrice ? kitPrice : undefined}
						productQuantity={quantity}
					/>
				</div>
			)}
			{showRemoveMessage && (
				<Notification
					type='toast'
					variant='success'
					title={t("cart.removedFromCart")}
				/>
			)}
		</>
	);
};

export default ProductKitForm;
